
import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Label } from '@/components/ui/label';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';

interface SettingsSectionProps {
  subscription: {
    subscribed: boolean;
    subscription_tier?: string;
    subscription_end?: string;
    payment_provider?: string;
    cancel_at_period_end?: boolean;
  } | null;
}

export const SettingsSection: React.FC<SettingsSectionProps> = ({ subscription }) => {
  const [cancelLoading, setCancelLoading] = useState(false);
  const { toast } = useToast();
  const { user, refreshSubscription } = useAuth();
  const navigate = useNavigate();

  const handleSubscribe = () => {
    navigate('/pricing');
  };

  const handleCancelSubscription = async () => {
    setCancelLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke('cancel-subscription', {
        headers: {
          Authorization: `Bearer ${(await supabase.auth.getSession()).data.session?.access_token}`,
        },
      });

      if (error) {
        throw new Error(error.message);
      }

      toast({
        title: "Subscription Cancelled",
        description: "Your subscription has been cancelled. You can continue using premium features until the end of your billing period.",
      });

      // Refresh subscription status
      if (refreshSubscription) {
        await refreshSubscription();
      }
    } catch (error) {
      console.error("Cancel subscription error:", error);
      toast({
        title: "Cancellation Failed",
        description: error instanceof Error ? error.message : "There was an issue cancelling your subscription. Please try again.",
        variant: "destructive",
      });
    } finally {
      setCancelLoading(false);
    }
  };

  // Helper function to format subscription end date
  const formatEndDate = (dateString?: string) => {
    if (!dateString) return 'end of period';
    return new Date(dateString).toLocaleDateString();
  };

  // Check if subscription period has ended
  const hasSubscriptionExpired = (endDate?: string) => {
    if (!endDate) return true;
    return new Date() > new Date(endDate);
  };

  // Check subscription states
  const isActiveNotCancelled = subscription?.subscribed === true && subscription?.cancel_at_period_end !== true;
  const isActiveCancelled = subscription?.subscribed === true && subscription?.cancel_at_period_end === true;
  const isExpired = subscription?.subscribed !== true || hasSubscriptionExpired(subscription?.subscription_end);

  return (
    <Card>
      <CardHeader>
        <CardTitle>Account Settings</CardTitle>
      </CardHeader>
      <CardContent className="space-y-6">
        <div>
          <h3 className="text-lg font-medium mb-4">Subscription</h3>
          <div className="space-y-4">
            {isActiveNotCancelled && (
              <div className="flex justify-between items-center p-4 border rounded-lg">
                <div>
                  <div className="font-medium">
                    Breeder Pro Plan - Active
                  </div>
                  <div className="text-sm text-gray-600">
                    Access to all premium features
                  </div>
                  {subscription?.subscription_end && (
                    <div className="text-xs text-gray-500">
                      Next billing: {formatEndDate(subscription.subscription_end)}
                    </div>
                  )}
                  {subscription?.payment_provider && (
                    <div className="text-xs text-blue-600 mt-1">
                      Payment provider: {subscription.payment_provider}
                    </div>
                  )}
                </div>
                <AlertDialog>
                  <AlertDialogTrigger asChild>
                    <Button 
                      variant="destructive"
                      disabled={cancelLoading}
                    >
                      {cancelLoading ? (
                        <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                      ) : null}
                      Cancel Subscription
                    </Button>
                  </AlertDialogTrigger>
                  <AlertDialogContent>
                    <AlertDialogHeader>
                      <AlertDialogTitle>Cancel Subscription</AlertDialogTitle>
                      <AlertDialogDescription>
                        Are you sure you want to cancel your subscription? You'll continue to have access to premium features until the end of your current billing period ({formatEndDate(subscription?.subscription_end)}).
                      </AlertDialogDescription>
                    </AlertDialogHeader>
                    <AlertDialogFooter>
                      <AlertDialogCancel>Keep Subscription</AlertDialogCancel>
                      <AlertDialogAction
                        onClick={handleCancelSubscription}
                        className="bg-red-600 hover:bg-red-700"
                      >
                        Yes, Cancel Subscription
                      </AlertDialogAction>
                    </AlertDialogFooter>
                  </AlertDialogContent>
                </AlertDialog>
              </div>
            )}

            {isActiveCancelled && (
              <div className="flex justify-between items-center p-4 border rounded-lg border-orange-200 bg-orange-50">
                <div>
                  <div className="font-medium text-orange-700">
                    Subscription Cancelled
                  </div>
                  <div className="text-sm text-orange-600">
                    Your subscription has been cancelled but remains active until {formatEndDate(subscription?.subscription_end)}
                  </div>
                  <div className="text-xs text-orange-500 mt-1">
                    You can still access all premium features during this period
                  </div>
                </div>
                {/* No button shown - user waits until subscription expires */}
              </div>
            )}

            {isExpired && (
              <div className="flex justify-between items-center p-4 border rounded-lg">
                <div>
                  <div className="font-medium">No Active Subscription</div>
                  <div className="text-sm text-gray-600">Subscribe to list cattle and access premium features</div>
                </div>
                <Button 
                  className="bg-green-700 hover:bg-green-800"
                  onClick={handleSubscribe}
                >
                  Subscribe Now
                </Button>
              </div>
            )}
          </div>
        </div>

        {user?.email && (
          <div>
            <h3 className="text-lg font-medium mb-4">Account Information</h3>
            <div className="space-y-4">
              <div>
                <Label htmlFor="email">Email Address</Label>
                <div className="mt-1 p-3 bg-gray-50 border border-gray-200 rounded-md text-gray-700">
                  {user.email}
                </div>
              </div>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
